import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { autobind } from 'core-decorators'
import { confirmable } from 'react-confirm'
import classNames from 'classnames'
import Icon from '../../../shared/icons'

import { formatMessage, FormattedMessage } from '../../../translations'

import { Dialog } from '../../../shared/components/Dialog'
import ContentLoadingBox from '../../../shared/components/ContentLoadingBox'
import GenevaButton from '../../../ui/components/GenevaButton'
import { store as articleStore } from '../..'
import { deepGet } from '../../../shared/obj'

const css = /* typeof window === 'undefined' ? {} : */require('../../styles.scss')

@confirmable
export default class ArticleCenshareLanguagesResultsDialog extends Component {

  static propTypes = {
    show: PropTypes.bool,            // indicates if the dialog is shown or not.
    proceed: PropTypes.func,         // call to close the dialog with promise resolved.
    cancel: PropTypes.func,          // call to close the dialog with promise rejected.
    dismiss: PropTypes.func,         // call to only close the dialog.
    confirmation: PropTypes.string,
    options: PropTypes.shape({
      article: PropTypes.object.isRequired,
      dialogResult: PropTypes.shape({
        toCreate: PropTypes.array.isRequired,
        toOpen: PropTypes.string.isRequired,
        censhareMap: PropTypes.array.isRequired
      }).isRequired,
      storeWithLangInfo: PropTypes.object.isRequired
    })
  }

  constructor(props) {
    super(props)

    this.state = {
      isLoading: true,
      isComplete: false,
      results: null,
      error: null,
      dialogTitle: formatMessage({ id: 'article.add-language-dialog.information-title' })
    }
  }

  componentDidMount() {
    // Start the article creation process immediately
    this.createArticlesInLanguage()
  }

  @autobind
  createArticlesInLanguage() {
    const { article, dialogResult, storeWithLangInfo } = this.props.options

    this.setState({ isLoading: true, error: null })

    articleStore.createArticlesInLanguage(
      article.id,
      dialogResult.toCreate,
      dialogResult.toOpen,
      storeWithLangInfo.current.id,
      { censhareMap: dialogResult.censhareMap }
    ).then(({ results, toOpenId }) => {

      // Process results and categorize them
      const processedResults = this.processCreationResults(results && results.body, toOpenId, dialogResult)

      this.setState({
        isLoading: false,
        isComplete: true,
        results: processedResults,
        dialogTitle: formatMessage({ id: 'article.add-language-dialog.censhare-results-complete' })
      })

    }).catch((error) => {
      console.error('Error creating articles in languages:', error)
      this.setState({
        isLoading: false,
        error: error.message || 'An error occurred while creating language variants',
        dialogTitle: formatMessage({ id: 'article.add-language-dialog.error-title' })
      })
    })
  }

  processCreationResults(dialogResult, toOpenId, originalDialogResult) {
    return {
      languageToOpenId: toOpenId,
      processedLanguages: dialogResult.map((lang) => {
        const placeholderReport = deepGet(lang, 'body.meta.placeholderReport') || { missing: [], found: [], all: [] }
        const langKey = deepGet(lang, 'body.data.createdIso') || 'unknown'

        return {
          language: langKey,
          success: lang.statusCode === 200,
          isCenshare: originalDialogResult.censhareMap.includes(langKey) || false,
          missingPlaceholdersCount: placeholderReport.missing.length || 0,
          foundPlaceholdersCount: placeholderReport.found.length || 0,
          allPlaceholdersCount: placeholderReport.all.length || 0,
        }
      })
    }
  }

  @autobind
  handleClose() {
    const { results } = this.state

    // Return navigation info if user created and wants to edit an article
    const navigationResult = results && results.languageToOpenId ? {
      navigateToId: results.languageToOpenId,
      navigateToLangKey: this.props.options.dialogResult.toOpen
    } : null

    this.props.proceed(navigationResult)
  }

  @autobind
  handleCancel() {
    this.props.cancel()
  }

  renderLoadingContent() {
    return (
      <div className="grid-content text-center">
        <ContentLoadingBox
          message={{ id: 'article.add-language-dialog.creating-languages' }}
          spinner
        />
      </div>
    )
  }

  renderErrorContent() {
    const { error } = this.state

    return (
      <div className="grid-content">
        <div className="callout alert">
          <h5><FormattedMessage id="article.add-language-dialog.error-title" /></h5>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  renderLanguageStatusText(langResult) {
    if (langResult.isCenshare) {
      if (langResult.missingPlaceholdersCount > 0) {
        return (
          <FormattedMessage
            id="article.add-language-dialog.censhare-elements-partial"
            values={{
              translated: langResult.foundPlaceholdersCount,
              total: langResult.allPlaceholdersCount
            }}
          />
        )
      }

      return (
        <FormattedMessage
          id="article.add-language-dialog.censhare-elements-translated"
          values={{
            translated: langResult.foundPlaceholdersCount,
            total: langResult.allPlaceholdersCount
          }}
        />
      )
    }

    // for non-censhare
    if (!langResult.success) {
      return (
        <FormattedMessage
          id="article.add-language-dialog.language-creation-error"
          values={{ language: langResult.language }}
        />
      )
    }
    return <FormattedMessage id="article.add-language-dialog.all-elements-translated" />
  }

  renderStatusIcon(langResult) {
    if (langResult.isCenshare && langResult.missingPlaceholdersCount > 0) {
      return <img className="warning-icon" role="presentation" src="/images/warning.svg" />
    }

    return <span className={css.successIcon}><Icon name="ion ion-ios-checkmark" /></span>
  }

  renderResultsContent() {
    const { results } = this.state

    if (!results) return null

    return (
      <div className="grid-content">
        <div className={css.resultsTitle}>
          <FormattedMessage id="article.add-language-dialog.article-created-in-languages" />
        </div>

        <div className={css.languageResultsList}>
          {results.processedLanguages.map(langResult => (
            <div
              key={langResult.language}
              className={css.languageResultRow}
            >
              <div className={css.languageCode}>{langResult.language}</div>
              <div className={css.languageStatus}>
                {this.renderLanguageStatusText(langResult)}
              </div>
              <div className={css.statusIcon}>
                {this.renderStatusIcon(langResult)}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  @autobind
  renderFooter() {
    const { isLoading } = this.state

    if (isLoading) {
      return (
        <div className="grid-content text-right">
          <GenevaButton
            type="button"
            className="small button"
            onClick={this.handleCancel}
          >
            <FormattedMessage id="article.add-language-dialog.cancel" />
          </GenevaButton>
        </div>
      )
    }

    return (
      <div className="grid-content text-right">
        <GenevaButton
          type="button"
          className="small button"
          onClick={this.handleClose}
        >
          <FormattedMessage id="article.add-language-dialog.close" />
        </GenevaButton>
      </div>
    )
  }

  render() {
    const { dialogTitle, isLoading, isComplete, error } = this.state

    return (
      <Dialog
        className={css.addLanguageDialog}
        overlayClassName=""
        isOpen={this.props.show}
        title={dialogTitle}
        onCancel={() => this.props.dismiss()}
        renderFooter={this.renderFooter}
        id="ArticleCenshareLanguagesResultsDialog"
      >
        {isLoading && this.renderLoadingContent()}
        {error && this.renderErrorContent()}
        {isComplete && !error && this.renderResultsContent()}
      </Dialog>
    )
  }
}
